import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiUpload, FiPackage, FiRefreshCw, FiGrid, FiTag, FiChevronDown, FiChevronUp, FiDollarSign, FiUsers, FiBox, FiTrendingDown } from 'react-icons/fi';
import { productAPI, supplierAPI, categoryAPI } from '../../services/api';
import { FiImage } from 'react-icons/fi';

const API_BASE_URL = process.env.API_BASE_URL || 'https://stelonne-market.online/backend/public'

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  
  // Formulaires
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    quantity: '',
    category_id: '',
    supplier_ids: [],
    images: []
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });

  // Statistiques calculées
  const totalProducts = products.length;
  const totalSuppliers = suppliers.length;
  const totalCategories = categories.length;
  const lowStockCount = products.filter(p => (p.quantity || 0) < 10).length;
  const averagePrice = products.length > 0 
    ? Math.round(products.reduce((sum, p) => sum + parseFloat(p.price || 0), 0) / products.length)
    : 0;

  // Déterminer la limite d'affichage des catégories
  const getCategoriesLimit = () => {
    if (typeof window === 'undefined') return 4;
    return window.innerWidth < 768 ? 4 : 6;
  };

  // Catégories à afficher
  const [categoriesLimit, setCategoriesLimit] = useState(getCategoriesLimit());
  useEffect(() => {
    const handleResize = () => {
      setCategoriesLimit(getCategoriesLimit());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displayedCategories = showAllCategories 
    ? categories 
    : categories.slice(0, categoriesLimit);

  const hasHiddenCategories = categories.length > categoriesLimit && !showAllCategories;
  const hiddenCategoriesCount = categories.length - categoriesLimit;

  // Charger les données initiales
  useEffect(() => {
    fetchAllData();
  }, []);

  // Filtrer les fournisseurs quand la catégorie change
  useEffect(() => {
    if (formData.category_id) {
      const filtered = suppliers.filter(s => {
        const supplierCategories = s.categories_ids || [];
        return supplierCategories.includes(parseInt(formData.category_id));
      });
      setFilteredSuppliers(filtered);
      
      if (formData.id_fournisseur && !filtered.some(s => s.id_fournisseur == formData.id_fournisseur)) {
        setFormData(prev => ({ ...prev, id_fournisseur: '' }));
      }
    } else {
      setFilteredSuppliers(suppliers);
    }
  }, [formData.id_categorie, suppliers]);

  const fetchAllData = async () => {
    setRefreshLoading(true);
    try {
      await Promise.all([
        fetchProducts(),
        fetchSuppliers(),
        fetchCategories()
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setRefreshLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getAll();
      console.log('📦 Products API response:', response);
      
      let productsData = [];
      
      if ( response && response.success && (Array.isArray(response.data) || Array.isArray(response.data.data))) {
        productsData = response.data || response.data.data || [];
      } else {
        console.warn('Format de réponse inattendu:', response);
        productsData = [];
      }
      
      console.log('📦 Products data:', productsData);
      setProducts(productsData);
      return productsData;
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      alert('Erreur lors du chargement des produits: ' + (error.response?.data?.message || error.message));
      return [];
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierAPI.getAllSupplier();
      console.log('🏢 Suppliers API response:', response);
      
      let suppliersData = [];
      
      if (response.data && response.data.success) {
        suppliersData = response.data.data || [];
      } else {
        console.warn('Format de réponse inattendu:', response);
        suppliersData = [];
      }
      
      setSuppliers(suppliersData);
      setFilteredSuppliers(suppliersData);
      return suppliersData;
    } catch (error) {
      console.error('❌ Error fetching suppliers:', error);
      alert('Erreur lors du chargement des fournisseurs: ' + (error.response?.data?.message || error.message));
      return [];
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getUniqueAll();
      console.log('📂 Categories API response:', response);
      
      let categoriesData = [];
      
      if (response.data && response.data.success) {
        categoriesData = response.data.data || [];
      } else {
        console.warn('Format de réponse inattendu:', response);
        categoriesData = [];
      }
      
      setCategories(categoriesData);
      return categoriesData;
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      alert('Erreur lors du chargement des catégories: ' + (error.response?.data?.message || error.message));
      return [];
    }
  };

  // Gestion des catégories
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setCategoryLoading(true);

    try {
      if (!categoryFormData.name.trim()) {
        alert('Le nom de la catégorie est obligatoire');
        setCategoryLoading(false);
        return;
      }

      if (editingCategory) {
        await categoryAPI.update(editingCategory.id, categoryFormData);
      } else {
        console.log('donnee soumise au backend', categoryFormData);
        await categoryAPI.create(categoryFormData);
      }
      
      await fetchCategories();
      closeCategoryModal();
    } catch (error) {
      console.error('❌ Error submitting category:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert('❌ Erreur lors de l\'opération: ' + errorMessage);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || '',
      description: category.description || ''
    });
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie? Les produits de cette catégorie deviendront "Non catégorisés".')) {
      try {
        await categoryAPI.delete(categoryId);
        await fetchCategories();
      } catch (error) {
        console.error('❌ Error deleting category:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
        alert('❌ Erreur lors de la suppression: ' + errorMessage);
      }
    }
  };

  const openNewCategoryModal = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      description: ''
    });
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      description: ''
    });
  };

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    setCategoryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getImageUrl = (imageName) => {
    if (!imageName) return null;
    
    if (imageName.startsWith('http') || imageName.startsWith('blob:')) {
      return imageName;
    }
    
    return `${API_BASE_URL}/storage/products/${imageName}`;
  };

  const getPrimaryImage = (images) => {
    if (!images || images.length === 0) return null;
    return images.find(img => img.is_primary) || images[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSupplierToggle = (supplierId) => {
    setFormData(prev => ({
      ...prev,
      supplier_ids: prev.supplier_ids.includes(supplierId)
        ? prev.supplier_ids.filter(id => id !== supplierId)
        : [...prev.supplier_ids, supplierId]
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    const newImages = files.map(file => {
      if (!file.type.startsWith('image/')) {
        alert(`Le fichier ${file.name} n'est pas une image valide`);
        return null;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`L'image ${file.name} ne doit pas dépasser 5MB`);
        return null;
      }
      
      return {
        file,
        preview: URL.createObjectURL(file),
        alt_text: '',
        is_primary: formData.images.length === 0
      };
    }).filter(img => img !== null);
    
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const setPrimaryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        is_primary: i === index
      }))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.name || !formData.price || !formData.quantity || !formData.category_id || !formData.supplier_ids || formData.supplier_ids.length === 0) {
        alert('Veuillez remplir tous les champs obligatoires');
        setLoading(false);
        return;
      }

      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim() || '',
        price: parseFloat(formData.price),
        quantity: parseInt(formData.quantity),
        category_id: parseInt(formData.category_id),
        supplier_ids: formData.supplier_ids.map(id => parseInt(id)),
        images: formData.images
      };

      console.log('📤 Données formatées:', productData);

      if (editingProduct) {
        await productAPI.update(editingProduct.id, productData);
      } else {
        await productAPI.create(productData);
      }
      
      await fetchProducts();
      closeModal();
    } catch (error) {
      console.error('❌ Error submitting product:', error);

      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        let errorMessage = 'Erreurs de validation:\n';
        Object.entries(errors).forEach(([field, messages]) => {
          errorMessage += `• ${field}: ${messages.join(', ')}\n`;
        });
        alert(errorMessage);
      } else if (error.response?.data?.message) {
        alert(`Erreur: ${error.response.data.message}`);
      } else {
        alert('Erreur: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    console.log('✏️ Editing product:', product);
    setEditingProduct(product);

    const images = product.images ? product.images.map(img => {
      if (img.id && img.image) {
        return {
          id: img.id,
          image: img.image,
          preview: getImageUrl(img.image),
          alt_text: img.alt_text || '',
          is_primary: img.is_primary || false,
        };
      }
      return img;
    }) : [];

    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      quantity: product.quantity || '',
      category_id: product.category_id || product.category?.id || '',
      supplier_ids: product.suppliers ? product.suppliers.map(s => s.id) : [],
      images: images
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (product) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le produit "${product.name}"?`)) {
      try {
        await productAPI.delete(product.id);
        console.log('✅ Produit supprimé avec succès!');
        await fetchProducts();
      } catch (error) {
        console.error('❌ Error deleting product:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
        alert('❌ Erreur lors de la suppression: ' + errorMessage);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      quantity: '',
      category_id: '',
      supplier_ids: [],
      images: []
    });
  };

  const filteredProducts = products.filter(product => {
    if (!searchTerm.trim()) {
      return true;
    }
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = product.name?.toLowerCase().includes(searchLower) || false;
    const categoryMatch = product.category?.name?.toLowerCase().includes(searchLower) || false;
    const descriptionMatch = product.description?.toLowerCase().includes(searchLower) || false;
    
    return nameMatch || categoryMatch || descriptionMatch;
  });

  // Fonction pour formater les nombres
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="p-4 md:p-6">
      {/* En-tête compact pour mobile */}
      <div className="mb-6">
        <div className="flex justify-between items-start gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">Gestion des Produits</h1>
            <p className="text-gray-600 text-sm md:text-base">
              {totalProducts} produit{totalProducts !== 1 ? 's' : ''} • {totalSuppliers} fournisseur{totalSuppliers !== 1 ? 's' : ''}
            </p>
          </div>
          
          {/* Bouton d'actualisation mobile */}
          <button
            onClick={fetchAllData}
            disabled={refreshLoading}
            className="p-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors md:hidden"
            title="Actualiser"
          >
            <FiRefreshCw className={`text-lg ${refreshLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Actions principales - Optimisé pour mobile */}
        <div className="flex flex-col xs:flex-row gap-3 mb-6">
          {/* Groupe de boutons sur mobile */}
          <div className="flex-1 flex flex-col sm:flex-row gap-3">
            <button
              onClick={fetchAllData}
              disabled={refreshLoading}
              className="hidden md:flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm flex-1"
            >
              <FiRefreshCw className={`text-base ${refreshLoading ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </button>
            
            <button
              onClick={openNewCategoryModal}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm flex-1"
            >
              <FiGrid className="text-base" />
              <span>Catégorie</span>
            </button>
            
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm flex-1"
            >
              <FiPlus className="text-base" />
              <span>Produit</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistiques compactes pour mobile */}
      <div className="grid grid-cols-2 xs:grid-cols-4 gap-3 md:gap-4 mb-6">
        {/* Produits totaux */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <FiPackage className="text-orange-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {formatNumber(totalProducts)}
              </p>
              <p className="text-xs text-gray-500">Produits</p>
            </div>
          </div>
        </div>

        {/* Fournisseurs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FiUsers className="text-blue-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {formatNumber(totalSuppliers)}
              </p>
              <p className="text-xs text-gray-500">Fournisseurs</p>
            </div>
          </div>
        </div>

        {/* Catégories */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <FiGrid className="text-green-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {formatNumber(totalCategories)}
              </p>
              <p className="text-xs text-gray-500">Catégories</p>
            </div>
          </div>
        </div>

        {/* Stock faible */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <FiTrendingDown className="text-red-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {formatNumber(lowStockCount)}
              </p>
              <p className="text-xs text-gray-500">Stock faible</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section catégories compacte */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiGrid className="text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800 text-sm md:text-base">Catégories disponibles</h2>
              <p className="text-xs text-gray-500">{totalCategories} catégories • {totalProducts} produits</p>
            </div>
          </div>
          <button
            onClick={openNewCategoryModal}
            className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
          >
            <FiPlus className="text-xs" />
            <span className="hidden xs:inline">Ajouter</span>
          </button>
        </div>
        
        {categories.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <FiGrid className="text-4xl text-gray-300 mx-auto mb-3" />
            <p className="text-sm">Aucune catégorie créée</p>
          </div>
        ) : (
          <>
            {/* Grille compacte des catégories */}
            <div className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-6 gap-3 mb-4">
              {displayedCategories.map((category) => {
                const productCount = products.filter(p => p.category_id === category.id).length;
                return (
                  <div 
                    key={category.id} 
                    className="border border-gray-200 rounded-lg p-3 hover:border-green-300 hover:bg-green-50 transition-colors cursor-pointer group relative"
                    onClick={() => handleEditCategory(category)}
                    title={category.name}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-gray-800 text-xs md:text-sm truncate flex-1 pr-1">
                        {category.name}
                      </h3>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCategory(category);
                          }}
                          className="p-0.5 text-blue-600 hover:bg-blue-100 rounded text-xs"
                          title="Modifier"
                        >
                          <FiEdit2 />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        {productCount} prod{productCount !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(category.id);
                        }}
                        className="text-xs text-red-500 hover:text-red-700 opacity-70 hover:opacity-100"
                        title="Supprimer"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bouton "Voir plus/Voir moins" */}
            {hasHiddenCategories && (
              <div className="text-center pt-3 border-t border-gray-100">
                <button
                  onClick={() => setShowAllCategories(!showAllCategories)}
                  className="text-green-600 hover:text-green-800 font-medium text-xs md:text-sm flex items-center justify-center gap-1 mx-auto"
                >
                  {showAllCategories ? (
                    <>
                      <FiChevronUp />
                      <span>Voir moins</span>
                    </>
                  ) : (
                    <>
                      <FiChevronDown />
                      <span>Voir plus ({hiddenCategoriesCount})</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Barre de recherche */}
      <div className="mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
          <input
            type="text"
            placeholder="Rechercher produits..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm md:text-base"
          />
        </div>
      </div>

      {/* Tableau des produits - Optimisé pour mobile */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm md:text-base">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Produit
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden sm:table-cell">
                  Catégorie
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Prix
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden xs:table-cell">
                  Stock
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => {
                  const primaryImage = getPrimaryImage(product.images);
                  const imageUrl = primaryImage ? getImageUrl(primaryImage.image) : null;
                  const stock = product.quantity || 0;
                  
                  return (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <img
                              src={imageUrl || `https://picsum.photos/64/64?random=${product.id}`}
                              alt={product.name}
                              className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover bg-gray-200"
                              onError={(e) => {
                                e.target.src = `https://picsum.photos/64/64?random=${product.id}`;
                              }}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 text-sm md:text-base truncate" title={product.name}>
                              {product.name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {product.suppliers?.slice(0, 2).map(s => s.name).join(', ') || 'Aucun fournisseur'}
                              {product.suppliers?.length > 2 && ` +${product.suppliers.length - 2}`}
                            </div>
                            {/* Catégorie en mobile */}
                            <div className="sm:hidden mt-1">
                              <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                {product.category?.name?.substring(0, 12) || 'Non cat.'}
                                {product.category?.name?.length > 12 ? '...' : ''}
                              </span>
                            </div>
                            {/* Stock en mobile très petit */}
                            <div className="xs:hidden mt-1">
                              <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                                stock > 10 
                                  ? 'bg-green-100 text-green-800' 
                                  : stock > 0 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {stock} unités
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {product.category?.name || 'Non catégorisé'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 text-sm md:text-base">
                          {parseFloat(product.price || 0).toLocaleString()} FCFA
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden xs:table-cell">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          stock > 10 
                            ? 'bg-green-100 text-green-800' 
                            : stock > 0 
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stock} unités
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1 md:gap-2">
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-1.5 md:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <FiEdit2 className="text-base md:text-lg" />
                          </button>
                          <button
                            onClick={() => handleDelete(product)}
                            className="p-1.5 md:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <FiTrash2 className="text-base md:text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-8 md:py-12 text-center">
                    <div className="flex flex-col items-center text-gray-500">
                      <FiPackage className="text-3xl md:text-4xl text-gray-300 mb-3" />
                      <p className="text-base md:text-lg font-medium mb-2">Aucun produit trouvé</p>
                      <p className="text-xs md:text-sm mb-4 text-center max-w-md">
                        {searchTerm 
                          ? 'Aucun résultat pour votre recherche' 
                          : 'Commencez par ajouter votre premier produit'}
                      </p>
                      {!searchTerm && (
                        <button 
                          onClick={() => setIsModalOpen(true)}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm md:text-base"
                        >
                          Ajouter un produit
                        </button>
                      )}
                    </div>
                  </td>       
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de catégorie */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                </h2>
                <button
                  onClick={closeCategoryModal}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <FiX className="text-lg" />
                </button>
              </div>

              <form onSubmit={handleCategorySubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom de la catégorie *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={categoryFormData.name}
                      onChange={handleCategoryChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex: Électronique"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={categoryFormData.description}
                      onChange={handleCategoryChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Description de la catégorie"
                      rows="3"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeCategoryModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={categoryLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                  >
                    {categoryLoading ? (
                      <>
                        <FiRefreshCw className="animate-spin" />
                        <span>Enregistrement...</span>
                      </>
                    ) : (
                      <>
                        <FiPlus />
                        <span>{editingCategory ? 'Mettre à jour' : 'Créer'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout/modification de produit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <FiX className="text-lg" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Colonne gauche */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du produit *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: iPhone 14 Pro Max"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Description du produit..."
                        rows="4"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prix (FCFA) *
                        </label>
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="0"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quantité *
                        </label>
                        <input
                          type="number"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="0"
                          min="0"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Colonne droite */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Catégorie *
                      </label>
                      <select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required
                      >
                        <option value="">Sélectionner une catégorie</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fournisseurs *
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {filteredSuppliers.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-2">
                            Aucun fournisseur disponible
                          </p>
                        ) : (
                          filteredSuppliers.map(supplier => (
                            <div key={supplier.id} className="flex items-center gap-2 mb-2 last:mb-0">
                              <input
                                type="checkbox"
                                id={`supplier-${supplier.id}`}
                                checked={formData.supplier_ids.includes(supplier.id)}
                                onChange={() => handleSupplierToggle(supplier.id)}
                                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                              />
                              <label
                                htmlFor={`supplier-${supplier.id}`}
                                className="text-sm text-gray-700 cursor-pointer flex-1"
                              >
                                {supplier.name}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Images
                      </label>
                      <div className="border border-dashed border-gray-300 rounded-lg p-4">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="cursor-pointer flex flex-col items-center justify-center py-4"
                        >
                          <FiUpload className="text-2xl text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">
                            Cliquez ou glissez-déposez vos images
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            Formats: JPG, PNG, GIF • Max: 5MB par image
                          </span>
                        </label>

                        {/* Aperçu des images */}
                        {formData.images.length > 0 && (
                          <div className="mt-4 grid grid-cols-3 gap-2">
                            {formData.images.map((img, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={img.preview}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-20 object-cover rounded border"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                  {img.is_primary ? (
                                    <span className="text-xs bg-orange-600 text-white px-2 py-1 rounded">
                                      Principale
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setPrimaryImage(index)}
                                      className="text-xs bg-white text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                                    >
                                      Définir
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="text-xs bg-white text-red-600 px-2 py-1 rounded hover:bg-gray-100"
                                  >
                                    Supprimer
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <FiRefreshCw className="animate-spin" />
                        <span>Enregistrement...</span>
                      </>
                    ) : (
                      <>
                        <FiPlus />
                        <span>{editingProduct ? 'Mettre à jour' : 'Créer le produit'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;